import { STATUS_TYPES_CLASSNAMES } from 'components/Status'

export const STEP_BANKING_SETUP = [
  {
    title: 'Business information and representatives',
    description:
      'Provide core legal and operational details to create the business account.',
    showStatus: true
  },
  {
    title: 'Underwriting',
    description: 'Supporting text',
    showStatus: false
  },
  {
    title: 'Bank account statements',
    description:
      'Processing statements helps us verify your transaction history and processing volume.',
    showStatus: false
  }
]

export const ONBOARDING_SESSION_STATUS = {
  INITIATED: 'initiated',
  ACCOUNT_CREATED: 'account_created',
  COMPLETED: 'completed'
}

export const ONBOARDING_SESSION_STATUS_TO_LABEL = {
  [ONBOARDING_SESSION_STATUS.INITIATED]: 'Pending',
  [ONBOARDING_SESSION_STATUS.ACCOUNT_CREATED]: 'Pending',
  [ONBOARDING_SESSION_STATUS.COMPLETED]: 'Completed'
}

export enum RESOLUTION_LINK_CONTACT_METHOD {
  EMAIL = 'email',
  PHONE = 'phone'
}

export const RESOLUTION_LINK_VISIBLE_ERRORS_COUNT = 3

export const RESOLUTION_LINK_ERROR_TEXT: Record<string, string> = {
  'failed-automatic-verification': 'The data provided could not be verified',
  'tax-id-mismatch': 'There was an issue verifying the SSN or TIN',
  'invalid-address': 'The address could not be verified',
  'address-restricted':
    'The address provided is not an acceptable residential address',
  'document-id-mismatch': 'The uploaded ID does not match information provided',
  'document-date-of-birth-mismatch':
    'The date of birth listed on the document does not match the date of birth provided',
  'document-name-mismatch':
    'The name listed on the document does not match the name provided',
  'document-number-mismatch':
    'A number listed on the document does not match the number provided',
  'document-incomplete': 'The document provided has incomplete information',
  'document-failed-risk': 'The document failed to meet a Moov requirement',
  'document-illegible': 'The uploaded document is not legible',
  'document-unsupported': 'The document type is not supported',
  'document-not-uploaded': 'A document was required, but not uploaded',
  'document-corrupt': 'The document was corrupt',
  'document-expired': 'The document was expired'
}

export const MOOV_BUSINESS_ACCOUNT_STATUS = {
  CREATED: 'created',
  IN_PROGRESS: 'verification_in_progress',
  VERIFIED: 'verified',
  RESTRICTED: 'account_restricted'
}

export const MOOV_BUSINESS_ACCOUNT_STATUS_TO_CLASSNAME = {
  [MOOV_BUSINESS_ACCOUNT_STATUS.CREATED]: STATUS_TYPES_CLASSNAMES.GRAY,
  [MOOV_BUSINESS_ACCOUNT_STATUS.IN_PROGRESS]: STATUS_TYPES_CLASSNAMES.YELLOW,
  [MOOV_BUSINESS_ACCOUNT_STATUS.VERIFIED]: STATUS_TYPES_CLASSNAMES.GREEN,
  [MOOV_BUSINESS_ACCOUNT_STATUS.RESTRICTED]: STATUS_TYPES_CLASSNAMES.RED
}

export const MOOV_BUSINESS_ACCOUNT_STATUS_TO_LABEL = {
  [MOOV_BUSINESS_ACCOUNT_STATUS.CREATED]: 'Created',
  [MOOV_BUSINESS_ACCOUNT_STATUS.IN_PROGRESS]: 'Verification in Progress',
  [MOOV_BUSINESS_ACCOUNT_STATUS.VERIFIED]: 'Verified',
  [MOOV_BUSINESS_ACCOUNT_STATUS.RESTRICTED]: 'Restricted'
}

export const BUSINESS_DETAILS_LABELS = {
  LEGAL_BUSINESS_NAME: 'Legal business name',
  DOING_BUSINESS_AS: 'Doing business as',
  INDUSTRY: 'Industry',
  SENDING_PAYMENTS: 'Sending payments',
  ACCEPTING_PAYMENTS: 'Accepting payments',
  BUSINESS_TYPE: 'Business type',
  GEOGRAPHIC_REACH: 'Geographic reach',
  ESTIMATED_MONTHLY_VOLUME: 'Estimated monthly volume',
  AVERAGE_TRANSACTION_AMOUNT: 'Average transaction amount',
  MAXIMUM_TRANSACTION_AMOUNT: 'Maximum transaction amount',
  BUSINESS_PRESENCE: 'Business presence',
  PENDING_LITIGATION: 'Pending litigation',
  CUSTOMER_TYPE: 'Customer type',
  COMPANY_DESCRIPTION: 'Company description',
  WEBSITE: 'Business website',
  EIN_OR_SSN: 'EIN (or SSN)',
  ADDRESS: 'Business address'
}

export const BUSINESS_TYPES = {
  SOLE_PROPRIETORSHIP: 'soleProprietorship',
  UNINCORPORATED_ASSOCIATION: 'unincorporatedAssociation',
  TRUST: 'trust',
  PUBLIC_CORPORATION: 'publicCorporation',
  PRIVATE_CORPORATION: 'privateCorporation',
  LLC: 'llc',
  PARTNERSHIP: 'partnership',
  UNINCORPORATED_NON_PROFIT: 'unincorporatedNonProfit',
  INCORPORATED_NON_PROFIT: 'incorporatedNonProfit',
  GOVERNMENT_ENTITY: 'governmentEntity'
} as const

export const BUSINESS_TYPES_LABELS = {
  [BUSINESS_TYPES.SOLE_PROPRIETORSHIP]: 'Sole Proprietorship',
  [BUSINESS_TYPES.UNINCORPORATED_ASSOCIATION]: 'Unincorporated Association',
  [BUSINESS_TYPES.TRUST]: 'Trust',
  [BUSINESS_TYPES.PUBLIC_CORPORATION]: 'Public Corporation',
  [BUSINESS_TYPES.PRIVATE_CORPORATION]: 'Private Corporation',
  [BUSINESS_TYPES.LLC]: 'LLC',
  [BUSINESS_TYPES.PARTNERSHIP]: 'Partnership',
  [BUSINESS_TYPES.UNINCORPORATED_NON_PROFIT]: 'Unincorporated Non Profit',
  [BUSINESS_TYPES.INCORPORATED_NON_PROFIT]: 'Incorporated Non Profit',
  [BUSINESS_TYPES.GOVERNMENT_ENTITY]: 'Government Entity'
}

export const MOOV_SUPPORT_TEAM_EMAIL =
  'support@community.ominnovations.health?subject=OM%20Community-Moov%20Business%20Account%20Verification%20Failed&body=Name%3A%0AHealth%20System%20Name%3A%0A---%0APlease%2C%20share%20your%20Legal%20business%20name%20and%20EIN%2FSSN%20that%20were%20used%20to%20create%20Business%20account%20if%20it%20is%20possible'

export const GEOGRAPHIC_REACH = {
  US_ONLY: 'us-only',
  US_AND_INTERNATIONAL: 'us-and-international',
  INTERNATIONAL_ONLY: 'international-only'
} as const

export const GEOGRAPHIC_REACH_LABELS: Record<string, string> = {
  [GEOGRAPHIC_REACH.US_ONLY]: 'U.S. only',
  [GEOGRAPHIC_REACH.US_AND_INTERNATIONAL]: 'U.S. and international',
  [GEOGRAPHIC_REACH.INTERNATIONAL_ONLY]: 'International only'
}

export const BUSINESS_PRESENCE = {
  COMMERCIAL_OFFICE: 'commercial-office',
  RETAIL_STOREFRONT: 'retail-storefront',
  HOME_BASED: 'home-based',
  ONLINE_ONLY: 'online-only',
  MOBILE_BUSINESS: 'mobile-business',
  MIXED_PRESENCE: 'mixed-presence'
} as const

export const BUSINESS_PRESENCE_LABELS: Record<string, string> = {
  [BUSINESS_PRESENCE.COMMERCIAL_OFFICE]: 'Commercial office',
  [BUSINESS_PRESENCE.RETAIL_STOREFRONT]: 'Retail storefront',
  [BUSINESS_PRESENCE.HOME_BASED]: 'Home based',
  [BUSINESS_PRESENCE.ONLINE_ONLY]: 'Online only',
  [BUSINESS_PRESENCE.MOBILE_BUSINESS]: 'Mobile business',
  [BUSINESS_PRESENCE.MIXED_PRESENCE]: 'Mixed presence'
}

export const PENDING_LITIGATION = {
  NONE: 'none',
  GOVERNMENT_ENFORCEMENT_OR_INVESTIGATION:
    'government-enforcement-or-investigation',
  FRAUD_OR_FINANCIAL_CRIME: 'fraud-or-financial-crime',
  CONSUMER_PROTECTION_OR_CLASS_ACTION: 'consumer-protection-or-class-action',
  DATA_BREACH_OR_PRIVACY: 'data-breach-or-privacy',
  BANKRUPTCY_OR_INSOLVENCY: 'bankruptcy-or-insolvency',
  EMPLOYMENT_OR_WORKPLACE_DISPUTES: 'employment-or-workplace-disputes',
  PERSONAL_INJURY_OR_MEDICAL: 'personal-injury-or-medical',
  INTELLECTUAL_PROPERTY: 'intellectual-property',
  OTHER: 'other'
} as const

export const PENDING_LITIGATION_LABELS: Record<string, string> = {
  [PENDING_LITIGATION.NONE]: 'None',
  [PENDING_LITIGATION.GOVERNMENT_ENFORCEMENT_OR_INVESTIGATION]:
    'Government enforcement or investigation',
  [PENDING_LITIGATION.FRAUD_OR_FINANCIAL_CRIME]: 'Fraud or financial crime',
  [PENDING_LITIGATION.CONSUMER_PROTECTION_OR_CLASS_ACTION]:
    'Consumer protection or class action',
  [PENDING_LITIGATION.DATA_BREACH_OR_PRIVACY]: 'Data breach or privacy',
  [PENDING_LITIGATION.BANKRUPTCY_OR_INSOLVENCY]: 'Bankruptcy or insolvency',
  [PENDING_LITIGATION.EMPLOYMENT_OR_WORKPLACE_DISPUTES]:
    'Employment or workplace disputes',
  [PENDING_LITIGATION.PERSONAL_INJURY_OR_MEDICAL]: 'Personal injury or medical',
  [PENDING_LITIGATION.INTELLECTUAL_PROPERTY]: 'Intellectual property',
  [PENDING_LITIGATION.OTHER]: 'Other'
}

export const MONTHLY_VOLUME_RANGE = {
  UNDER_10K: 'under-10k',
  FROM_10K_TO_50K: '10k-50k',
  FROM_50K_TO_100K: '50k-100k',
  FROM_100K_TO_250K: '100k-250k',
  FROM_250K_TO_500K: '250k-500k',
  FROM_500K_TO_1M: '500k-1m',
  FROM_1M_TO_5M: '1m-5m',
  OVER_5M: 'over-5m'
} as const

export const MONTHLY_VOLUME_RANGE_LABELS: Record<string, string> = {
  [MONTHLY_VOLUME_RANGE.UNDER_10K]: 'Less than $10,000',
  [MONTHLY_VOLUME_RANGE.FROM_10K_TO_50K]: '$10,000 – $50,000',
  [MONTHLY_VOLUME_RANGE.FROM_50K_TO_100K]: '$50,000 – $100,000',
  [MONTHLY_VOLUME_RANGE.FROM_100K_TO_250K]: '$100,000 – $250,000',
  [MONTHLY_VOLUME_RANGE.FROM_250K_TO_500K]: '$250,000 – $500,000',
  [MONTHLY_VOLUME_RANGE.FROM_500K_TO_1M]: '$500,000 – $1,000,000',
  [MONTHLY_VOLUME_RANGE.FROM_1M_TO_5M]: '$1,000,000 – $5,000,000',
  [MONTHLY_VOLUME_RANGE.OVER_5M]: 'Over $5,000,000'
}
